// src/data/gettingStartedQuestions.ts

import { Question } from '../types';

export const gettingStartedQuestions: Question[] = [
  // ========================================
  // SECTION 1: Profile & Timeline (6 questions)
  // ========================================
  {
    id: 'GS1',
    section: 'profile',
    question: 'What type of organization are you building for?',
    type: 'single',
    options: [
      { value: 'startup', label: 'Healthcare Startup', description: 'Digital health, telehealth, healthtech SaaS', points: 0 },
      { value: 'hospital', label: 'Hospital or Clinic', description: 'Traditional healthcare provider', points: 0 },
      { value: 'vendor', label: 'Healthcare Technology Vendor', description: 'Selling software/services to covered entities', points: 0 },
      { value: 'research', label: 'Research Organization', description: 'Clinical research, academic medical center', points: 0 },
      { value: 'other', label: 'Other', description: '', points: 0 },
    ],
    regulation: 'Organization Profile',
    required: true,
    weight: 1,
  },
  {
    id: 'GS2',
    section: 'profile',
    question: 'What is your current stage?',
    type: 'single',
    options: [
      { value: 'planning', label: 'Planning / Pre-launch', description: 'Haven\'t started building yet', points: 0 },
      { value: 'building', label: 'Actively Building', description: 'Development in progress', points: 0 },
      { value: 'mvp', label: 'MVP Complete', description: 'First version built, not yet compliant', points: 0 },
      { value: 'beta', label: 'Beta / Early Customers', description: 'Have users but need to formalize compliance', points: 0 },
    ],
    regulation: 'Development Stage',
    required: true,
    weight: 1,
  },
  {
    id: 'GS3',
    section: 'profile',
    question: 'When do you plan to launch or need to be HIPAA compliant?',
    type: 'single',
    options: [
      { value: '3months', label: 'Within 3 months', description: 'Aggressive timeline', points: 0 },
      { value: '6months', label: '3-6 months', description: 'Standard timeline', points: 0 },
      { value: '12months', label: '6-12 months', description: 'Long runway', points: 0 },
      { value: 'flexible', label: '12+ months or flexible', description: 'No hard deadline', points: 0 },
    ],
    regulation: 'Implementation Timeline',
    required: true,
    weight: 1,
  },
  {
    id: 'GS4',
    section: 'profile',
    question: 'What is your organization size?',
    type: 'single',
    options: [
      { value: 'solo', label: 'Solo founder or 1-2 people', description: '', points: 0 },
      { value: 'small', label: 'Small team (3-10 people)', description: '', points: 0 },
      { value: 'medium', label: 'Medium (11-50 people)', description: '', points: 0 },
      { value: 'large', label: 'Large (50+ people)', description: '', points: 0 },
    ],
    regulation: 'Organization Size',
    required: true,
    weight: 1,
  },
  {
    id: 'GS5',
    section: 'profile',
    question: 'What is your implementation approach preference?',
    type: 'single',
    options: [
      { value: 'diy', label: 'DIY - Build it ourselves', description: 'We have technical expertise and time', points: 0 },
      { value: 'guided', label: 'Guided - We build with expert guidance', description: 'Want help but will implement ourselves', points: 0 },
      { value: 'done', label: 'Done-for-you - Hire experts to implement', description: 'Want turnkey implementation', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: 'Implementation Preferences',
    required: true,
    weight: 1,
  },
  {
    id: 'GS6',
    section: 'profile',
    question: 'What is your compliance budget range?',
    type: 'single',
    options: [
      { value: 'minimal', label: 'Minimal - Use free/open-source tools', description: '<$5k', points: 0 },
      { value: 'standard', label: 'Standard - AWS costs + some tools', description: '$5k-20k', points: 0 },
      { value: 'platform', label: 'Platform - Compliance automation platform', description: '$20k-50k', points: 0 },
      { value: 'full', label: 'Full service - Platform + consulting', description: '$50k+', points: 0 },
    ],
    regulation: 'Budget Planning',
    required: true,
    weight: 1,
  },

  // ========================================
  // SECTION 2: Architecture & Tech Stack (8 questions)
  // ========================================
  {
    id: 'GS7',
    section: 'architecture',
    question: 'Which cloud provider will you use?',
    type: 'single',
    options: [
      { value: 'aws', label: 'Amazon Web Services (AWS)', description: '', points: 0 },
      { value: 'azure', label: 'Microsoft Azure', description: '', points: 0 },
      { value: 'gcp', label: 'Google Cloud Platform (GCP)', description: '', points: 0 },
      { value: 'multi', label: 'Multi-cloud', description: '', points: 0 },
      { value: 'onprem', label: 'On-premises / Self-hosted', description: '', points: 0 },
      { value: 'unsure', label: 'Haven\'t decided yet', description: '', points: 0 },
    ],
    regulation: 'Cloud Infrastructure',
    required: true,
    weight: 1,
  },
  {
    id: 'GS8',
    section: 'architecture',
    question: 'Will you use AI/ML in your healthcare application?',
    type: 'single',
    options: [
      { value: 'yes_clinical', label: 'Yes - Clinical decision support', description: 'AI helps with diagnosis, treatment recommendations', points: 0 },
      { value: 'yes_admin', label: 'Yes - Administrative / Non-clinical', description: 'Chatbots, documentation, billing, scheduling', points: 0 },
      { value: 'yes_both', label: 'Yes - Both clinical and administrative', description: '', points: 0 },
      { value: 'no', label: 'No AI/ML planned', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: 'AI/ML Usage Planning',
    required: true,
    weight: 1,
  },
  {
    id: 'GS9',
    section: 'architecture',
    question: 'If using AI, which vendor(s) will you use? (Select all that apply)',
    type: 'multiple',
    options: [
      { value: 'aws_bedrock', label: 'AWS Bedrock', description: '', points: 0 },
      { value: 'azure_openai', label: 'Azure OpenAI', description: '', points: 0 },
      { value: 'openai', label: 'OpenAI API (ChatGPT, GPT-4)', description: '', points: 0 },
      { value: 'google', label: 'Google Vertex AI / Gemini', description: '', points: 0 },
      { value: 'anthropic', label: 'Anthropic Claude', description: '', points: 0 },
      { value: 'self_hosted', label: 'Self-hosted open-source models', description: '', points: 0 },
      { value: 'unsure', label: 'Haven\'t decided yet', description: '', points: 0 },
    ],
    regulation: 'AI Vendor Selection',
    required: false,
    conditionalOn: { questionId: 'GS8', requiredValue: ['yes_clinical', 'yes_admin', 'yes_both'] },
    weight: 1,
  },
  {
    id: 'GS10',
    section: 'architecture',
    question: 'What type of healthcare data will you handle?',
    type: 'multiple',
    options: [
      { value: 'clinical', label: 'Clinical data (diagnoses, medications, lab results)', description: '', points: 0 },
      { value: 'demographics', label: 'Patient demographics (name, DOB, address)', description: '', points: 0 },
      { value: 'financial', label: 'Financial / Billing information', description: '', points: 0 },
      { value: 'genomic', label: 'Genomic / Genetic data', description: '', points: 0 },
      { value: 'imaging', label: 'Medical imaging (X-rays, MRIs, etc.)', description: '', points: 0 },
      { value: 'behavioral', label: 'Behavioral health / Mental health data', description: '', points: 0 },
      { value: 'other', label: 'Other PHI', description: '', points: 0 },
    ],
    regulation: 'PHI Data Types',
    required: true,
    weight: 1,
  },
  {
    id: 'GS11',
    section: 'architecture',
    question: 'Will you have a mobile application?',
    type: 'single',
    options: [
      { value: 'yes_native', label: 'Yes - Native mobile apps (iOS/Android)', description: '', points: 0 },
      { value: 'yes_web', label: 'Yes - Mobile web only (responsive website)', description: '', points: 0 },
      { value: 'both', label: 'Both native apps and mobile web', description: '', points: 0 },
      { value: 'no', label: 'No - Web only (desktop)', description: '', points: 0 },
    ],
    regulation: 'Mobile Platform Planning',
    required: true,
    weight: 1,
  },
  {
    id: 'GS12',
    section: 'architecture',
    question: 'Will you store PHI in databases?',
    type: 'single',
    options: [
      { value: 'yes_cloud', label: 'Yes - Cloud database (RDS, Aurora, DynamoDB, etc.)', description: '', points: 0 },
      { value: 'yes_self', label: 'Yes - Self-managed database', description: '', points: 0 },
      { value: 'both', label: 'Both cloud and self-managed', description: '', points: 0 },
      { value: 'no', label: 'No databases - passing through only', description: '', points: 0 },
    ],
    regulation: 'Database Architecture',
    required: true,
    weight: 1,
  },
  {
    id: 'GS13',
    section: 'architecture',
    question: 'Will you integrate with Electronic Health Records (EHR) systems?',
    type: 'single',
    options: [
      { value: 'yes_epic', label: 'Yes - Epic integration', description: '', points: 0 },
      { value: 'yes_cerner', label: 'Yes - Cerner integration', description: '', points: 0 },
      { value: 'yes_fhir', label: 'Yes - FHIR API integrations', description: '', points: 0 },
      { value: 'yes_multiple', label: 'Yes - Multiple EHR systems', description: '', points: 0 },
      { value: 'no', label: 'No EHR integrations', description: '', points: 0 },
    ],
    regulation: 'EHR Integration Planning',
    required: true,
    weight: 1,
  },
  {
    id: 'GS14',
    section: 'architecture',
    question: 'Will you operate in multiple states?',
    type: 'single',
    options: [
      { value: 'single', label: 'Single state only', description: '', points: 0 },
      { value: 'multi', label: 'Multiple states', description: '', points: 0 },
      { value: 'national', label: 'Nationwide (all 50 states)', description: '', points: 0 },
      { value: 'international', label: 'International (outside US)', description: '', points: 0 },
    ],
    regulation: 'Geographic Scope',
    required: true,
    weight: 1,
  },

  // ========================================
  // SECTION 3: Use Cases & Patient Interaction (5 questions)
  // ========================================
  {
    id: 'GS15',
    section: 'usecase',
    question: 'Is your application patient-facing?',
    type: 'single',
    options: [
      { value: 'yes_direct', label: 'Yes - Patients directly use our app', description: 'Telehealth, patient portal, health app', points: 0 },
      { value: 'provider', label: 'No - Provider-facing only', description: 'EHR, practice management, clinical tools', points: 0 },
      { value: 'both', label: 'Both patient-facing and provider-facing', description: '', points: 0 },
      { value: 'b2b', label: 'B2B - We serve other healthcare organizations', description: '', points: 0 },
    ],
    regulation: 'Application Type',
    required: true,
    weight: 1,
  },
  {
    id: 'GS16',
    section: 'usecase',
    question: 'Will patients be able to access their own health records through your system?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes - Full patient access to records', description: '', points: 0 },
      { value: 'partial', label: 'Partial - Limited access (e.g., lab results only)', description: '', points: 0 },
      { value: 'no', label: 'No - Providers access only', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.524 - Patient Right of Access',
    required: true,
    weight: 1,
  },
  {
    id: 'GS17',
    section: 'usecase',
    question: 'Will you transmit PHI to external parties (other than your own systems)?',
    type: 'multiple',
    options: [
      { value: 'ehr', label: 'Yes - To EHR systems', description: '', points: 0 },
      { value: 'labs', label: 'Yes - To labs / diagnostic centers', description: '', points: 0 },
      { value: 'providers', label: 'Yes - To other healthcare providers', description: '', points: 0 },
      { value: 'payers', label: 'Yes - To insurance payers', description: '', points: 0 },
      { value: 'patients', label: 'Yes - To patients (via email, SMS, etc.)', description: '', points: 0 },
      { value: 'no', label: 'No external transmission', description: '', points: 0 },
    ],
    regulation: 'Transmission Security Requirements',
    required: true,
    weight: 1,
  },
  {
    id: 'GS18',
    section: 'usecase',
    question: 'Will you use third-party vendors that access PHI? (Select all that apply)',
    type: 'multiple',
    options: [
      { value: 'email', label: 'Email service (SendGrid, Mailgun, etc.)', description: '', points: 0 },
      { value: 'sms', label: 'SMS/texting (Twilio, etc.)', description: '', points: 0 },
      { value: 'analytics', label: 'Analytics (Mixpanel, Amplitude, etc.)', description: '', points: 0 },
      { value: 'crm', label: 'CRM (Salesforce, HubSpot, etc.)', description: '', points: 0 },
      { value: 'payment', label: 'Payment processor', description: '', points: 0 },
      { value: 'monitoring', label: 'Monitoring/logging (Datadog, Sentry, etc.)', description: '', points: 0 },
      { value: 'none', label: 'No third-party vendors with PHI access', description: '', points: 0 },
    ],
    regulation: 'Business Associate Requirements',
    required: true,
    weight: 1,
  },
  {
    id: 'GS19',
    section: 'usecase',
    question: 'Will you need to provide breach notification capabilities?',
    type: 'single',
    options: [
      { value: 'yes_ready', label: 'Yes - We\'ll build notification system now', description: '', points: 0 },
      { value: 'yes_later', label: 'Yes - But we\'ll add it later', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure what this requires', description: '', points: 0 },
    ],
    regulation: '§164.404 - Breach Notification',
    required: true,
    weight: 1,
  },

  // ========================================
  // SECTION 4: Safeguards Planning (15 questions)
  // ========================================
  {
    id: 'GS20',
    section: 'safeguards',
    question: 'Will you implement multi-factor authentication (MFA)?',
    type: 'single',
    options: [
      { value: 'yes_all', label: 'Yes - For all users', description: 'Recommended (now REQUIRED per 2026 update)', points: 0 },
      { value: 'yes_admins', label: 'Yes - For admins/privileged users only', description: '', points: 0 },
      { value: 'no', label: 'No - Password-only', description: 'Not recommended', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '2026 HIPAA Update - MFA Mandatory',
    required: true,
    weight: 1,
  },
  {
    id: 'GS21',
    section: 'safeguards',
    question: 'How will you encrypt PHI at rest?',
    type: 'single',
    options: [
      { value: 'kms', label: 'Cloud KMS (AWS KMS, Azure Key Vault, etc.)', description: 'Recommended', points: 0 },
      { value: 'app', label: 'Application-level encryption', description: '', points: 0 },
      { value: 'both', label: 'Both KMS and application-level', description: 'Most secure', points: 0 },
      { value: 'none', label: 'No encryption at rest', description: 'Must document why (addressable)', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.312(a)(2)(iv) - Encryption at Rest',
    required: true,
    weight: 1,
  },
  {
    id: 'GS22',
    section: 'safeguards',
    question: 'How will you encrypt PHI in transit?',
    type: 'single',
    options: [
      { value: 'tls', label: 'TLS 1.2+ for all connections', description: 'Required', points: 0 },
      { value: 'tls13', label: 'TLS 1.3 only', description: 'Most secure', points: 0 },
      { value: 'vpn', label: 'VPN + TLS', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.312(e)(2)(ii) - Transmission Security',
    required: true,
    weight: 1,
  },
  {
    id: 'GS23',
    section: 'safeguards',
    question: 'Will you implement audit logging for all PHI access?',
    type: 'single',
    options: [
      { value: 'yes_comprehensive', label: 'Yes - Comprehensive logging (all access, 6-year retention)', description: 'Required', points: 0 },
      { value: 'yes_basic', label: 'Yes - Basic logging (critical events only)', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure what level needed', description: '', points: 0 },
    ],
    regulation: '§164.312(b) - Audit Controls (REQUIRED)',
    required: true,
    weight: 1,
  },
  {
    id: 'GS24',
    section: 'safeguards',
    question: 'Will you implement automatic session timeout?',
    type: 'single',
    options: [
      { value: 'yes_15', label: 'Yes - 15 minutes or less', description: 'Recommended', points: 0 },
      { value: 'yes_30', label: 'Yes - 30 minutes', description: '', points: 0 },
      { value: 'yes_custom', label: 'Yes - Custom timeout per user role', description: '', points: 0 },
      { value: 'no', label: 'No automatic timeout', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.312(a)(2)(iii) - Automatic Logoff',
    required: true,
    weight: 1,
  },
  {
    id: 'GS25',
    section: 'safeguards',
    question: 'Will you implement network segmentation to isolate PHI systems?',
    type: 'single',
    options: [
      { value: 'yes_vpc', label: 'Yes - Dedicated VPC/VNET for PHI systems', description: 'Recommended (required per 2026 update)', points: 0 },
      { value: 'yes_subnets', label: 'Yes - Separate subnets', description: '', points: 0 },
      { value: 'no', label: 'No network segmentation', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '2026 HIPAA Update - Network Segmentation Required',
    required: true,
    weight: 1,
  },
  {
    id: 'GS26',
    section: 'safeguards',
    question: 'How will you handle data backups?',
    type: 'single',
    options: [
      { value: 'automated', label: 'Automated daily backups with 30+ day retention', description: 'Recommended', points: 0 },
      { value: 'weekly', label: 'Weekly backups', description: '', points: 0 },
      { value: 'manual', label: 'Manual backups only', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(7)(ii)(A) - Data Backup Plan (REQUIRED)',
    required: true,
    weight: 1,
  },
  {
    id: 'GS27',
    section: 'safeguards',
    question: 'Will you have a disaster recovery plan?',
    type: 'single',
    options: [
      { value: 'yes_tested', label: 'Yes - Documented and regularly tested', description: 'Recommended', points: 0 },
      { value: 'yes_doc', label: 'Yes - Documented but not tested yet', description: '', points: 0 },
      { value: 'planning', label: 'Will create during implementation', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure what this requires', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(7)(ii)(B) - Disaster Recovery Plan (REQUIRED)',
    required: true,
    weight: 1,
  },
  {
    id: 'GS28',
    section: 'safeguards',
    question: 'Will you conduct annual security training for all workforce members?',
    type: 'single',
    options: [
      { value: 'yes_annual', label: 'Yes - Annual training with documentation', description: 'Required', points: 0 },
      { value: 'yes_onboarding', label: 'Yes - Onboarding only', description: '', points: 0 },
      { value: 'planning', label: 'Will set up during implementation', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(5) - Security Awareness and Training',
    required: true,
    weight: 1,
  },
  {
    id: 'GS29',
    section: 'safeguards',
    question: 'Will you designate a HIPAA Security Official?',
    type: 'single',
    options: [
      { value: 'yes_dedicated', label: 'Yes - Dedicated role', description: '', points: 0 },
      { value: 'yes_cto', label: 'Yes - CTO or technical leader', description: 'Common for startups', points: 0 },
      { value: 'yes_founder', label: 'Yes - Founder / CEO', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure who yet', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(2) - Assigned Security Responsibility (REQUIRED)',
    required: true,
    weight: 1,
  },
  {
    id: 'GS30',
    section: 'safeguards',
    question: 'Will you conduct a comprehensive risk analysis?',
    type: 'single',
    options: [
      { value: 'yes_nist', label: 'Yes - Using NIST framework', description: 'Recommended', points: 0 },
      { value: 'yes_template', label: 'Yes - Using a template/checklist', description: '', points: 0 },
      { value: 'yes_consultant', label: 'Yes - Hiring consultant to conduct it', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure how to approach this', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(1)(ii)(A) - Risk Analysis (REQUIRED)',
    required: true,
    weight: 1,
  },
  {
    id: 'GS31',
    section: 'safeguards',
    question: 'How will you handle workforce terminations?',
    type: 'single',
    options: [
      { value: 'automated', label: 'Automated offboarding (revoke access immediately)', description: 'Recommended', points: 0 },
      { value: 'manual', label: 'Manual checklist process', description: '', points: 0 },
      { value: 'planning', label: 'Will establish during implementation', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(3)(ii)(C) - Termination Procedures',
    required: true,
    weight: 1,
  },
  {
    id: 'GS32',
    section: 'safeguards',
    question: 'Will you have an incident response plan for security breaches?',
    type: 'single',
    options: [
      { value: 'yes_detailed', label: 'Yes - Detailed plan with notification templates', description: 'Recommended', points: 0 },
      { value: 'yes_basic', label: 'Yes - Basic procedures', description: '', points: 0 },
      { value: 'planning', label: 'Will create during implementation', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure what this requires', description: '', points: 0 },
    ],
    regulation: '§164.308(a)(6) - Security Incident Procedures (REQUIRED)',
    required: true,
    weight: 1,
  },
  {
    id: 'GS33',
    section: 'safeguards',
    question: 'If using AI, will you log all AI prompts and responses containing PHI?',
    type: 'single',
    options: [
      { value: 'yes_comprehensive', label: 'Yes - All AI interactions logged with 6-year retention', description: 'Recommended', points: 0 },
      { value: 'yes_basic', label: 'Yes - Basic logging', description: '', points: 0 },
      { value: 'no', label: 'No AI logging', description: '', points: 0 },
      { value: 'na', label: 'Not using AI', description: '', points: 0 },
    ],
    regulation: '§164.312(b) - Audit Controls for AI Systems',
    required: false,
    conditionalOn: { questionId: 'GS8', requiredValue: ['yes_clinical', 'yes_admin', 'yes_both'] },
    weight: 1,
  },
  {
    id: 'GS34',
    section: 'safeguards',
    question: 'If using AI, how will you ensure models don\'t train on your PHI?',
    type: 'single',
    options: [
      { value: 'contractual', label: 'Vendor contractual guarantees + zero-retention settings', description: 'Recommended', points: 0 },
      { value: 'vendor', label: 'Vendor guarantees only', description: '', points: 0 },
      { value: 'selfhosted', label: 'Self-hosted models (full control)', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure how to ensure this', description: '', points: 0 },
      { value: 'na', label: 'Not using AI', description: '', points: 0 },
    ],
    regulation: 'AI Data Usage Restrictions',
    required: false,
    conditionalOn: { questionId: 'GS8', requiredValue: ['yes_clinical', 'yes_admin', 'yes_both'] },
    weight: 1,
  },

  // ========================================
  // SECTION 5: Physical Safeguards (3 questions) - CONDITIONAL
  // ========================================
  {
    id: 'GS35',
    section: 'physical',
    question: 'Will you have a physical office or facility with workstations accessing PHI?',
    type: 'single',
    options: [
      { value: 'yes', label: 'Yes - Physical office/facility', description: '', points: 0 },
      { value: 'no', label: 'No - Fully remote/cloud-based', description: '', points: 0 },
      { value: 'hybrid', label: 'Hybrid - Some physical, some remote', description: '', points: 0 },
    ],
    regulation: 'Physical Safeguards Applicability',
    required: true,
    weight: 1,
  },
  {
    id: 'GS36',
    section: 'physical',
    question: 'If you have a physical facility, how will you control access?',
    type: 'single',
    options: [
      { value: 'badge', label: 'Badge/keycard system with logging', description: 'Recommended', points: 0 },
      { value: 'basic', label: 'Basic locks and sign-in sheets', description: '', points: 0 },
      { value: 'planning', label: 'Will establish during implementation', description: '', points: 0 },
      { value: 'na', label: 'No physical facility', description: '', points: 0 },
    ],
    regulation: '§164.310(a)(1) - Facility Access Controls',
    required: false,
    conditionalOn: { questionId: 'GS35', requiredValue: ['yes', 'hybrid'] },
    weight: 1,
  },
  {
    id: 'GS37',
    section: 'physical',
    question: 'How will you dispose of devices/media containing PHI?',
    type: 'single',
    options: [
      { value: 'certified', label: 'Certified destruction/wiping service', description: 'Recommended', points: 0 },
      { value: 'software', label: 'Software wiping (NIST 800-88 standards)', description: '', points: 0 },
      { value: 'planning', label: 'Will establish procedures', description: '', points: 0 },
      { value: 'unsure', label: 'Not sure yet', description: '', points: 0 },
    ],
    regulation: '§164.310(d)(1) - Device and Media Controls',
    required: true,
    weight: 1,
  },
];
